import { useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';

import { chronicleDeviceStateAtom } from '@/atoms/chronicle';
import { chronicleBridge } from '@/tauri/chronicle';
import type { FirmwareFileInfo } from '@/tauri/chronicle/types';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';

/** Start OTA from a local firmware binary (dev-only). */
export interface LocalFirmwareUpdateArgs {
  filePath: string;
}

/** Start OTA from a remote download URL. */
export interface RemoteFirmwareUpdateArgs {
  downloadUrl: string;
  targetVersion: string;
  deviceModel?: string;
}

export type StartFirmwareUpdateArgs = LocalFirmwareUpdateArgs | RemoteFirmwareUpdateArgs;

/**
 * Hook for Chronicle OTA firmware update **actions only**.
 *
 * Returns action functions (select file, start update, cancel) and local UI state
 * (isSelectingFirmware). All OTA lifecycle state (status, progress, error) lives
 * in the device atom and is read via `useChronicleDeviceStatus()`.
 */
export function useChronicleOta() {
  const setDeviceState = useSetAtom(chronicleDeviceStateAtom);
  const [isSelectingFirmware, setIsSelectingFirmware] = useState(false);

  const selectFirmwareFile = useCallback(
    async (dialogTitle?: string): Promise<FirmwareFileInfo | null> => {
      setIsSelectingFirmware(true);
      try {
        const fileInfo = await chronicleBridge.selectFirmwareFile(dialogTitle);
        if (!fileInfo) return null;

        logger.info({
          msg: '[useChronicleOta] Firmware file selected',
          fileName: fileInfo.fileName,
        });

        setDeviceState((prev) => ({ ...prev, selectedFirmwareFile: fileInfo }));
        return fileInfo;
      } finally {
        setIsSelectingFirmware(false);
      }
    },
    [setDeviceState]
  );

  const startUpdate = useCallback(async (args: StartFirmwareUpdateArgs) => {
    try {
      logger.info({
        msg: '[useChronicleOta] Starting firmware update',
        args,
      });
      if ('filePath' in args) {
        await chronicleBridge.startFirmwareUpdate({
          filePath: args.filePath,
        });
      } else {
        await chronicleBridge.startFirmwareUpdate({
          downloadUrl: args.downloadUrl,
          targetVersion: args.targetVersion,
          deviceModel: args.deviceModel,
        });
      }
    } catch (error) {
      logger.error({
        msg: '[useChronicleOta] Failed to start firmware update',
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }, []);

  const cancelUpdate = useCallback(async () => {
    try {
      logger.info({ msg: '[useChronicleOta] Cancelling firmware update' });
      await chronicleBridge.cancelFirmwareUpdate();
    } catch (error) {
      logger.error({
        msg: '[useChronicleOta] Failed to cancel firmware update',
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }, []);

  return {
    selectFirmwareFile,
    startUpdate,
    cancelUpdate,
    isSelectingFirmware,
  };
}
